import * as SwitchPrimitives from "@radix-ui/react-switch";
import { type FC, forwardRef, useEffect, useState } from "react";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";

export type SwitchState = "unchecked" | "checked";

export type SwitchProps = {
    state: SwitchState;
    isLoading?: boolean;
    disabled?: boolean;
    onToggle: (checked: boolean) => Promise<void> | void;
    id?: string;
    "data-testid"?: string;
    "aria-label"?: string;
    "data-tracking-id"?: string;
};

export const Switch = forwardRef<HTMLDivElement, SwitchProps>(
    (
        {
            state,
            isLoading = false,
            disabled,
            onToggle,
            id,
            "data-testid": dataTestId,
            "aria-label": ariaLabel,
            "data-tracking-id": dataTrackingId,
            ...rest
        },
        ref,
    ) => {
        const checked = state === "checked";

        // This ensures we only animate state transitions, and not the initial render.
        const [initialState] = useState(state);
        const [hasTransitioned, setHasTransitioned] = useState(false);
        useEffect(() => {
            if (state !== initialState) {
                setHasTransitioned(true);
            }
        }, [state, initialState]);

        const animate = hasTransitioned || isLoading || state !== initialState;

        return (
            <div className="flex w-9 justify-center" ref={ref} {...rest}>
                <SwitchPrimitives.Root
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={onToggle}
                    aria-label={ariaLabel}
                    className={cn("h-5 w-9 cursor-pointer rounded-full bg-black/10 dark:bg-white/10 disabled:cursor-default", {
                        "transition-[width] duration-300": animate,
                        "opacity-50": disabled && !isLoading,
                        "data-[state=checked]:bg-content-success disabled:data-[state=checked]:bg-content-success":
                            !isLoading,
                    })}
                    id={id}
                    data-testid={dataTestId || id}
                    data-tracking-id={dataTrackingId}
                >
                    <SwitchPrimitives.Thumb
                        className={cn(
                            "flex size-5 items-center justify-center data-[state=checked]:translate-x-[16px]",
                            {
                                "transition-transform duration-300": animate,
                            },
                        )}
                    >
                        <ThumbSVG className="size-5" animating={isLoading} state={state} />
                    </SwitchPrimitives.Thumb>
                </SwitchPrimitives.Root>
            </div>
        );
    },
);

Switch.displayName = "Switch";

const ThumbSVG: FC<PropsWithClassName<{ animating: boolean; state: SwitchState }>> = ({
    className,
    animating,
    state,
}) => {
    /**
     * Implementation notes:
     * The basic idea is to have a circle with a partial stroke and have it spin using CSS. The stroke is transparent
     * when the thumb is not "loading".
     *
     * You can't define if a border should be drawn "outside" of a circle, so to have full control of the border we're
     * using two circle: one for the thumb and one for the border. The border-circle is transparent and uses a stroke with
     * a stroke-dasharray to achieve the "partial border" effect.
     *
     */
    return (
        <svg
            width="25"
            height="25"
            viewBox="0 0 25 25"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={cn(
                {
                    // Blue spinner when loading toward "on", orange when loading toward "off"
                    "animate-spin text-content-brand duration-1000": animating && state === "checked",
                    "animate-spin text-content-yield duration-1000": animating && state !== "checked",
                },
                className,
            )}
        >
            <circle
                cx="12.5"
                cy="12.5"
                r={10}
                className={cn({
                    "fill-[rgb(var(--ona-white))]": !animating,
                    "fill-[rgb(var(--ona-white))] dark:fill-[rgb(var(--ona-gray-600))]": animating,
                })}
            />
            <circle
                cx="12.5"
                cy="12.5"
                r={11.5}
                fill="none"
                stroke={animating ? "currentColor" : "transparent"}
                strokeWidth={2}
                // 18 is the length of the stroke
                // 76 is the circumference of the circle to ensure we only render "one" stroke
                strokeDasharray={"18 76"}
            />
        </svg>
    );
};
